import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { CatalogPourbackReason } from "../../../models/CatalogPourbackReason";
import { upsertPourbackReason } from "../../../repositories/CatalogPourbackReasonRepository";
import { usePourbackOptionsFunctions } from "../hooks/usePourbackOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { PourbackModal } from "./PourbackModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogPourbackReasonsFromAPI: Function;
  isLoading: boolean;
  pourbackReasonId: String | undefined;
};

export const TablePourbackReason = ({
  getCatalogPourbackReasonsFromAPI,
  isLoading,
  pourbackReasonId,
}: Props) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [pourback, setPourback] = useState<CatalogPourbackReason | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [error, setError] = useState("");

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewPourback = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setPourback(null);
    navigate(`/configuration/caster-pourback-reasons/new`);
  };

  const { getMenuOptions } = usePourbackOptionsFunctions({
    setPourback,
    setShowModal,
    handleNewPourback,
  });

  // one-click retire/restore from the grid; the modal stays for renames
  const handleToggleEnabled = async (row: CatalogPourbackReason) => {
    const resp = await upsertPourbackReason({ ...row, Enabled: !row.Enabled });
    if (resp.ok) {
      setNotificationMessage(
        `'${row.PourbackReasonName}' was ${row.Enabled ? "disabled" : "enabled"}`,
      );
      await getCatalogPourbackReasonsFromAPI();
    } else {
      setError(resp.message);
    }
  };

  const { rows, columnDefs } = useTableData({
    setPourback,
    setShowModal,
    showContextMenu,
    onToggleEnabled: handleToggleEnabled,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setPourback(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setPourback(event.data as CatalogPourbackReason);
      setShowModal("edit");
      navigate(
        `/configuration/caster-pourback-reasons/${
          (event.data as CatalogPourbackReason).PourbackReasonId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/caster-pourback-reasons/${
          (event.data as CatalogPourbackReason).PourbackReasonId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "pourbackContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setPourback(null);
    navigate(`/configuration/caster-pourback-reasons/`);
  };

  useEffect(() => {
    if (pourbackReasonId) {
      if (pourbackReasonId === "new") {
        handleNewPourback();
      } else {
        let pourbackReasonNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if (
            (n.data as CatalogPourbackReason).PourbackReasonId ===
            Number(pourbackReasonId)
          ) {
            pourbackReasonNode = n;
            return true;
          }
        });

        if (pourbackReasonNode!) {
          gridApi?.ensureIndexVisible(
            pourbackReasonNode.rowIndex ?? 0,
            "middle",
          );
          pourbackReasonNode.setSelected(true);
          setPourback(pourbackReasonNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "pourbackContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Pourback Reasons
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogPourbackReason>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              loading={isLoading}
              getRowId={(params) => String(params.data.PourbackReasonId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton
            label="POURBACK REASON"
            onClick={handleNewPourback}
            sizes={{ md: 3 }}
          />
        </Grid>
      </Grid>
      <PourbackModal
        pourbackReason={pourback}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogPourbackReasonsFromAPI}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Pourback"
      />
    </>
  );
};
